/* eslint-disable max-lines */
import type { Type } from '@angular/core';
import { createNgModule, inject, Injectable, Injector } from '@angular/core';
import type { Params } from '@angular/router';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import type { Subscription } from 'rxjs';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  from,
  Observable,
  of as observableOf,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  share,
  shareReplay,
  skip,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { AccountSelectors } from '@core/account-data';
import { FullCandidateSelectors } from '@core/candidate-data';
import { filterBoolean } from '@core/common-legacy';
import type { CandidateVisitedSnapshot } from '@core/dashboard-types';
import { injectDevFlag } from '@core/dev-flags-logic';
import { DevFlag } from '@core/dev-flags-types';
import { FeatureSpecFacade } from '@core/feature-spec-logic';
import { FeatureCode } from '@core/feature-spec-types';
import { I18nService } from '@core/i18n-legacy';
import { MailboxCache } from '@core/mailbox-data';
import { AssistantStartersContextService } from '@core/tellent-assistant-logic';
import type { ConversationStartersContext } from '@core/tellent-assistant-types';
import {
  DialogService,
  ProtectedControlsDialogComponent,
  RtPlatform,
} from '@core/user-interface-legacy';
import { deepEqual, macroTask } from '@core/utils-legacy';
import { PageTitleService } from '@recruitee/common-logic';
import { VisitedEntitiesHistoryService } from '@recruitee/dashboard-logic';
import { EvaluationDraftService } from '@recruitee/evaluations-logic';
import { EvaluationPanelService } from '@recruitee/evaluations-smart-ui';
import type { EvaluationPanelData, EvaluationPanelOpenOptions } from '@recruitee/evaluations-types';

import { candidateResolver } from './resolvers/candidate.resolver';
import { selectCandidateData } from '../features/candidate/store/feature';

type CandidateDialogData =
  import('../features/candidate/candidate-dialog/candidate-dialog.component').CandidateDialogData;
type CandidateDialogComponent =
  import('../features/candidate/candidate-dialog/candidate-dialog.component').CandidateDialogComponent;

@Injectable({ providedIn: 'root' })
export class CandidateDialogService {
  private readonly dialogService = inject(DialogService);
  private readonly platform = inject(RtPlatform);
  private readonly mailboxCache = inject(MailboxCache);
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly pageTitleService = inject(PageTitleService);
  private readonly i18nService = inject(I18nService);
  private readonly injector = inject(Injector);
  private readonly visitedEntitiesHistoryService = inject<
    VisitedEntitiesHistoryService<CandidateVisitedSnapshot>
  >(VisitedEntitiesHistoryService<CandidateVisitedSnapshot>);
  private readonly assistantContext = inject(AssistantStartersContextService);
  private readonly evaluationPanel = inject(EvaluationPanelService);
  private readonly evaluationDraft = inject(EvaluationDraftService);
  private readonly featureSpecFacade = inject(FeatureSpecFacade);
  private readonly hasPositionsFeature = this.featureSpecFacade.selectIsEnabled(
    FeatureCode.Positions,
  );
  private readonly hasEvaluationPanel = injectDevFlag(DevFlag.EvaluationImprovements);

  private readonly ngOnDestroy$ = new Subject<void>();
  private readonly isOpen$ = new Subject<boolean>();
  private readonly candidateData$ = new BehaviorSubject<CandidateDialogData>(<any>null);

  private readonly conversationStartersContext$: Observable<ConversationStartersContext | null> =
    combineLatest([
      this.isOpen$.pipe(startWith(false)),
      this.store.select(selectCandidateData),
    ]).pipe(
      map(([isOpen, candidate]) =>
        isOpen && candidate
          ? ((<const>{
              type: 'recruitment_candidate',
              params: { candidateId: candidate.id, candidateName: candidate.name },
            }) satisfies ConversationStartersContext)
          : null,
      ),
    );

  private candidateDialogComponent$ = from(import('../features/candidate/candidate.module')).pipe(
    map(({ CandidateDialogComponent, RtCandidateFeatureModule }) => ({
      CandidateDialogComponent,
      candidateFeatureModuleRef: createNgModule(RtCandidateFeatureModule, this.injector),
    })),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  private pendingPanelData?: EvaluationPanelData;
  private pendingPanelOptions?: EvaluationPanelOpenOptions;
  private currentDialogEl?: HTMLElement;
  private panelContextSubscription?: Subscription;
  private visitedCandidateId: number | null = null;

  constructor() {
    this.candidateDialogComponent$.pipe(take(1)).subscribe();

    this.conversationStartersContext$
      .pipe(
        distinctUntilChanged((a, b) => deepEqual(a, b)),
        switchMap(ctx =>
          ctx ? new Observable<never>(() => this.assistantContext.push(ctx, 'overlay')) : EMPTY,
        ),
      )
      .subscribe();
  }

  public init(): void {
    this.isOpen$
      .pipe(
        distinctUntilChanged(),
        switchMap(isOpen =>
          this.candidateDialogComponent$.pipe(
            switchMap(({ CandidateDialogComponent, candidateFeatureModuleRef }) =>
              isOpen
                ? this.openDialog(
                    CandidateDialogComponent,
                    candidateFeatureModuleRef.injector,
                    this.candidateData$,
                  )
                : EMPTY,
            ),
          ),
        ),
      )
      .subscribe();

    combineLatest([
      this.store.select(selectCandidateData),
      this.isOpen$,
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)),
    ])
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(([candidate, isOpen]) => {
        if (!candidate || !isOpen) return;

        const profileString = this.i18nService.translate('app.candidate.profile');
        const pageTitle = `${candidate.name} - ${profileString}`;
        this.pageTitleService.setCustomTitle(pageTitle);
      });

    combineLatest([
      this.route.queryParams.pipe(filterBoolean),
      this.store.select(AccountSelectors.currentCompany).pipe(filterBoolean, take(1)),
    ])
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(([queryParams]) =>
        queryParams.candidate ? this.open(queryParams) : this.close(),
      );
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public open(queryParams: Params): void {
    const openInterviewInPanel = this.hasEvaluationPanel && !!queryParams.openInterview;

    const interviewParams: Partial<CandidateDialogData> = openInterviewInPanel
      ? {}
      : {
          openInterview: queryParams.openInterview ? true : undefined,
          interviewOfferId: queryParams.interviewOfferId
            ? +queryParams.interviewOfferId
            : undefined,
          interviewTalentPoolId: queryParams.interviewTalentPoolId
            ? +queryParams.interviewTalentPoolId
            : undefined,
          interviewTemplateId: queryParams.interviewTemplateId
            ? +queryParams.interviewTemplateId
            : undefined,
          interviewEdit: queryParams.interviewEdit ? !!queryParams.interviewEdit : undefined,
          interviewResultId: queryParams.interviewResultId
            ? +queryParams.interviewResultId
            : undefined,
          interviewResultRequestId: queryParams.interviewResultRequestId
            ? +queryParams.interviewResultRequestId
            : undefined,
          fromHiddenEvaluations: queryParams.fromHiddenEvaluations
            ? !!queryParams.fromHiddenEvaluations
            : undefined,
        };

    this.candidateData$.next({
      candidateId: +queryParams.candidate,
      tab: queryParams.tab ? queryParams.tab : undefined,
      tabKey: queryParams.tabKey ? queryParams.tabKey : undefined,
      offerId: queryParams.offerId ? queryParams.offerId : undefined,
      ...interviewParams,
      matchId: queryParams.matchId ? +queryParams.matchId : undefined,
    });
    this.isOpen$.next(true);

    // Record "recently visited" once per candidate, not on every tab switch
    // (open() re-runs on each queryParams change, e.g. the active tab).
    const candidateId = +queryParams.candidate;
    if (candidateId !== this.visitedCandidateId) {
      this.visitedCandidateId = candidateId;
      candidateResolver(this.store, selectCandidateData)
        .pipe(take(1), takeUntil(this.ngOnDestroy$.asObservable()))
        .subscribe(candidate => this.visitedEntitiesHistoryService.updateHistory(candidate));
    }

    if (openInterviewInPanel) {
      this.openInterviewPanel(queryParams);
    } else if (this.hasEvaluationPanel && !this.evaluationPanel.isOpen && !this.pendingPanelData) {
      this.restoreEvaluationPanelDraft(+queryParams.candidate);
    }

    candidateResolver(this.store, selectCandidateData)
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(candidate => this.visitedEntitiesHistoryService.updateHistory(candidate));
  }

  public close(): void {
    this.closeEvaluationPanel();
    this.isOpen$.next(false);
    this.visitedCandidateId = null;
  }

  private openDialog(
    component: Type<CandidateDialogComponent>,
    injector: Injector,
    data$: BehaviorSubject<CandidateDialogData>,
  ) {
    return new Observable(observer => {
      const destroy$ = new Subject<void>();
      const data = data$.value;
      const dialogRef = this.dialogService.openCustom(
        component,
        {
          showCloseButton: false,
          disableClose: false,
          containerOptions: {
            hasPadding: false,
          },
          injector,
          closeGuard: () => {
            const { protectedControls } = dialogRef.componentInstance;
            if (protectedControls.locked) {
              return this.dialogService
                .openCustom(ProtectedControlsDialogComponent, {}, {})
                .afterClosed.pipe(
                  tap(result => {
                    if (result) {
                      this.mailboxCache.remove(
                        this.mailboxCache.key('candidate', data.candidateId),
                      );
                    }
                  }),
                  share(),
                );
            } else {
              return observableOf(true);
            }
          },
        },
        this.getInitialData(data$.value),
      );

      this.currentDialogEl = dialogRef.overlayRef.overlayElement;

      if (this.pendingPanelData) {
        this.openEvaluationPanel(
          this.pendingPanelData,
          this.currentDialogEl,
          this.pendingPanelOptions,
        );
        this.pendingPanelData = undefined;
        this.pendingPanelOptions = undefined;
      }

      data$.pipe(skip(1), takeUntil(destroy$)).subscribe(data => {
        dialogRef.componentInstance.setDialogData(data, true);
      });
      dialogRef.afterClosed.pipe(take(1)).subscribe(() => {
        macroTask(() => this.clearUrlParams());
        this.closeEvaluationPanel();
        this.currentDialogEl = undefined;
        this.isOpen$.next(false);
        observer.complete();
      });
      return () => {
        destroy$.next();
        destroy$.complete();
        dialogRef.close();
      };
    });
  }

  private getInitialData<T extends CandidateDialogData>(data: T): CandidateDialogData {
    return {
      candidateId: data.candidateId,
      offerId: data.offerId,
      tab: data.tab,
      tabKey: data.tabKey,
      openInterview: data.openInterview,
      interviewOfferId: data.interviewOfferId,
      interviewTalentPoolId: data.interviewTalentPoolId,
      interviewTemplateId: data.interviewTemplateId,
      interviewEdit: data.interviewEdit,
      interviewResultId: data.interviewResultId,
      interviewResultRequestId: data.interviewResultRequestId,
      fromHiddenEvaluations: data.fromHiddenEvaluations,
      matchId: data.matchId ? +data.matchId : undefined,
    };
  }

  private clearUrlParams() {
    this.router.navigate([], {
      queryParams: {
        candidate: null,
        offerId: null,
        tab: null,
        tabKey: null,
        subtab: null,
        messagesMode: null,
        smsThread: null,
        openInterview: null,
        interviewOfferId: null,
        interviewTalentPoolId: null,
        interviewTemplateId: null,
        interviewEdit: null,
        interviewResultId: null,
        interviewResultRequestId: null,
        fromHiddenEvaluations: null,
        matchId: null,
      } satisfies Omit<Record<keyof CandidateDialogData, null>, 'candidateId'> & {
        candidate: null;
      },
      queryParamsHandling: 'merge',
      replaceUrl: false,
    });
  }

  private clearInterviewUrlParams() {
    this.router.navigate([], {
      queryParams: {
        openInterview: null,
        interviewOfferId: null,
        interviewTalentPoolId: null,
        interviewTemplateId: null,
        interviewEdit: null,
        interviewResultId: null,
        interviewResultRequestId: null,
        quickEvaluation: null,
        fromHiddenEvaluations: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private openInterviewPanel(queryParams: Params): void {
    let panelOfferId: number | undefined;

    if (queryParams.interviewOfferId) {
      panelOfferId = +queryParams.interviewOfferId;
    } else if (queryParams.interviewTalentPoolId) {
      panelOfferId = +queryParams.interviewTalentPoolId;
    }

    const panelData: EvaluationPanelData = {
      candidateId: +queryParams.candidate,
      offerId: panelOfferId,
      templateId: queryParams.interviewTemplateId ? +queryParams.interviewTemplateId : undefined,
      resultId: queryParams.interviewResultId ? +queryParams.interviewResultId : undefined,
      resultRequestId: queryParams.interviewResultRequestId
        ? +queryParams.interviewResultRequestId
        : undefined,
      edit: queryParams.interviewEdit ? !!queryParams.interviewEdit : undefined,
      quickEvaluation: queryParams.quickEvaluation ? true : undefined,
      hasPositionsFeature: this.hasPositionsFeature(),
    };

    const draft = this.evaluationDraft.getDraft(panelData.candidateId);
    const options: EvaluationPanelOpenOptions | undefined =
      draft && this.isSameEvaluation(draft.data, panelData) ? { restore: draft.form } : undefined;

    if (this.currentDialogEl) {
      this.openEvaluationPanel(panelData, this.currentDialogEl, options);
    } else {
      this.pendingPanelData = panelData;
      this.pendingPanelOptions = options;
    }

    this.clearInterviewUrlParams();
  }

  private isSameEvaluation(a: EvaluationPanelData, b: EvaluationPanelData): boolean {
    if (a.candidateId !== b.candidateId) return false;
    if (!!a.quickEvaluation !== !!b.quickEvaluation) return false;
    if (!!a.edit !== !!b.edit) return false;
    if (a.resultId !== b.resultId) return false;

    if (a.quickEvaluation) return true;

    return a.templateId === b.templateId && a.offerId === b.offerId;
  }

  private restoreEvaluationPanelDraft(candidateId: number): void {
    const draft = this.evaluationDraft.getDraft(candidateId);
    if (!draft) return;

    const options: EvaluationPanelOpenOptions = { minimized: true, restore: draft.form };

    if (this.currentDialogEl) {
      this.openEvaluationPanel(draft.data, this.currentDialogEl, options);
    } else {
      this.pendingPanelData = draft.data;
      this.pendingPanelOptions = options;
    }
  }

  private openEvaluationPanel(
    panelData: EvaluationPanelData,
    dialogEl: HTMLElement,
    options?: EvaluationPanelOpenOptions,
  ): void {
    this.evaluationPanel.open(panelData, dialogEl, options);
    this.watchEvaluationPanelContext(panelData);
  }

  private watchEvaluationPanelContext(panelData: EvaluationPanelData): void {
    this.panelContextSubscription?.unsubscribe();

    let candidateMatched = false;

    this.panelContextSubscription = combineLatest([
      this.store.select(selectCandidateData),
      this.store.select(
        FullCandidateSelectors.currentUserAvailablePlacements({
          candidateId: panelData.candidateId,
        }),
      ),
      this.store.select(FullCandidateSelectors.commonUsersGrouped(panelData.candidateId)),
    ]).subscribe(([candidate, availablePlacements, mentionUsers]) => {
      if (!this.evaluationPanel.isOpen) {
        this.closeEvaluationPanel();
        return;
      }

      if (!candidate) return;

      if (candidate.id !== panelData.candidateId) {
        if (candidateMatched) {
          this.closeEvaluationPanel();
        }
        return;
      }

      candidateMatched = true;
      this.evaluationPanel.updateContext(
        { id: candidate.id, placements: candidate.placements },
        availablePlacements
          .map(placement => placement.offer || placement.talentPool)
          .filter(offer => !!offer),
        mentionUsers,
      );
    });
  }

  private closeEvaluationPanel(): void {
    this.panelContextSubscription?.unsubscribe();
    this.panelContextSubscription = undefined;
    this.evaluationPanel.close();
  }
}
